import {
  Grid,
  IconButton,
  TextField,
  LinearProgress,
  Card,
  CardContent,
  Stack,
  Typography,
  Tooltip,
} from "@mui/material";
import Box from "@mui/material/Box";
import { DataGrid, GridPagination } from "@mui/x-data-grid";
import { BiEdit } from "react-icons/bi";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CapitalAmount,
  setBalanceDialog,
  setCapitalAmountTransactionsDialog,
  setSearchBody,
} from "../../../../../app/slices/capitalAmountSlice";
import dayjs from "dayjs";
import CapitalAmountBalanceForm from "../../form/balance";
import { GiPayMoney, GiReceiveMoney } from "react-icons/gi";
import { MdOutlineClear } from "react-icons/md";
import { FaMoneyCheckAlt } from "react-icons/fa";
import Permission from "../../../../../components/userPermission/userPermission";

const CapitalAmountTable = () => {
  const dispatch = useDispatch();

  const _CapitalAmounts = useSelector(
    (state) => state.capitalAmount.capitalAmounts,
  );
  const loading = useSelector(
    (state) => state.capitalAmount.capitalAmounts.loading,
  );
  const _BalanceDialog = useSelector(
    (state) => state.capitalAmount.balance.dialog,
  );

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(15);
  const [currentPage, setCurrentPage] = useState(0);

  const columns = [
    {
      field: "index",
      headerName: "#",
      flex: 0.2,
    },
    {
      field: "name",
      headerName: "الاسم",
      flex: 1,
    },
    {
      field: "balance",
      headerName: "الرصيد",
      flex: 1,
      renderCell: (params) =>
        parseInt(params.row.balance ?? 0).toLocaleString(),
    },
    {
      field: "note",
      headerName: "الملاحظات",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.row.note}>
          <Typography variant="body" noWrap>
            {params.row.note}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "created_at",
      headerName: "التاريخ",
      flex: 1,
      renderCell: (params) => (
        <Stack spacing={1}>
          <Typography>
            {dayjs(params.row.created_at).format("YYYY-MM-DD")}
          </Typography>
          <Typography>
            {dayjs(params.row.created_at).format("hh:mm a")}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "operations",
      headerName: "العمليات",
      flex: 1,
      renderCell: (params) => {
        return (
          <>
            <Permission permission="capital_amount_edit">
              <Tooltip title="اضافة رصيد">
                <IconButton
                  variant="contained"
                  color="info"
                  onClick={() =>
                    dispatch(
                      setBalanceDialog({
                        id: params.row.id,
                        type: "deposit",
                      }),
                    )
                  }
                >
                  <GiReceiveMoney />
                </IconButton>
              </Tooltip>
            </Permission>
            <Permission permission="capital_amount_edit">
              <Tooltip title="سحب رصيد">
                <IconButton
                  variant="contained"
                  color="error"
                  onClick={() =>
                    dispatch(
                      setBalanceDialog({
                        id: params.row.id,
                        type: "withdraw",
                      }),
                    )
                  }
                >
                  <GiPayMoney />
                </IconButton>
              </Tooltip>
            </Permission>
            <Tooltip title="المعاملات المالية">
              <IconButton
                variant="contained"
                onClick={() =>
                  dispatch(setCapitalAmountTransactionsDialog(params.row.id))
                }
              >
                <FaMoneyCheckAlt />
              </IconButton>
            </Tooltip>
            <Permission permission="capital_amount_edit">
              <Tooltip title="تعديل">
                <IconButton
                  onClick={() => dispatch(CapitalAmount.getById(params.row.id))}
                >
                  <BiEdit />
                </IconButton>
              </Tooltip>
            </Permission>
          </>
        );
      },
    },
  ];

  useEffect(() => {
    const timeout = setTimeout(() => {
      dispatch(CapitalAmount.getAll({ search: search, skip: 0, take: 15 }));
      dispatch(setSearchBody({ search: search, skip: 0, take: 15 }));
    }, 500);
    return () => clearTimeout(timeout);
  }, [dispatch, search]);

  function CustomFooter() {
    return (
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Typography color="primary" sx={{ marginX: 2 }}>
          المجموع الكلي : {(_CapitalAmounts.total_amount ?? 0).toLocaleString()}{" "}
          (IQD)
        </Typography>
        <GridPagination />
      </Stack>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box>{_BalanceDialog && <CapitalAmountBalanceForm />}</Box>
        <Grid container rowSpacing={1} columnSpacing={{}}>
          <Grid item xs={3}>
            <TextField
              fullWidth
              size="small"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              label="بحث عن الاسم"
            />
          </Grid>
          <Grid item xs={2.5}>
            <Stack direction="row" spacing={0}>
              <Tooltip title="الغاء الفلتر">
                <IconButton
                  onClick={() => {
                    setSearch("");
                    setPage(15);
                    setCurrentPage(0);
                  }}
                >
                  <MdOutlineClear />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ height: "70vh", width: "100%" }}>
              <DataGrid
                rows={
                  _CapitalAmounts.data?.map((x, index) => ({
                    ...x,
                    index: index + 1,
                  })) ?? []
                }
                columns={columns}
                pageSize={page}
                page={currentPage}
                rowCount={_CapitalAmounts.total}
                loading={loading}
                rowHeight={50}
                pagination
                paginationMode="server"
                selectionModel={[]}
                onPageChange={(newPage) => {
                  dispatch(
                    CapitalAmount.getAll({
                      search: search,
                      skip: newPage * page,
                      take: page,
                    }),
                  );
                  setCurrentPage(newPage);
                }}
                rowsPerPageOptions={[15, 25, 50, 100]}
                onPageSizeChange={(newPageSize) => {
                  dispatch(
                    CapitalAmount.getAll({
                      search: search,
                      skip: 0,
                      take: newPageSize,
                    }),
                  );
                  setPage(newPageSize);
                }}
                components={{
                  LoadingOverlay: LinearProgress,
                  Footer: CustomFooter,
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CapitalAmountTable;
